import { createElement, useCallback, useEffect, useRef, type HTMLAttributes } from "react";

/**
 * The one place that speaks to screen readers (a11y-11, spec 07 › Anuncios).
 *
 * A `role="status"` / `aria-live` region that is mounted together with its
 * text is not announced by every screen reader (VoiceOver and TalkBack only
 * read changes to a region they already knew), and a whole card marked
 * `aria-live` re-reads everything on each change. So screens never mount a
 * live region of their own: they call `announce(text)` or `useAnnounce(text)`
 * and the words land in one of two regions (polite and assertive) that live
 * as direct children of <body> from the first render of the app.
 *
 * The regions carry `data-overlay-keep`, so an open Dialog or Sheet (which
 * makes the rest of the page `inert`, overlay-utils) does not silence them.
 */
export type AnnouncePoliteness = "polite" | "assertive";

const REGION_ATTRIBUTE = "data-live-region";
/** Delay between clearing and filling: the same words twice are said twice. */
const FILL_DELAY_MS = 50;
/** A region created just now needs a beat before its first change is heard. */
const FRESH_REGION_DELAY_MS = 250;

const SR_ONLY_STYLE =
  "position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0";

const timers: Partial<Record<AnnouncePoliteness, ReturnType<typeof setTimeout>>> = {};

/** A real DOM to speak into (not the server render nor a stubbed `document`). */
function hasDom(): boolean {
  return typeof document !== "undefined" && typeof document.querySelector === "function" && Boolean(document.body);
}

function findRegion(politeness: AnnouncePoliteness): HTMLElement | null {
  return document.querySelector<HTMLElement>(`[${REGION_ATTRIBUTE}="${politeness}"]`);
}

function createRegion(politeness: AnnouncePoliteness): HTMLElement {
  const region = document.createElement("div");
  region.setAttribute(REGION_ATTRIBUTE, politeness);
  region.setAttribute("data-overlay-keep", "");
  region.setAttribute("data-testid", `live-announcer-${politeness}`);
  region.setAttribute("aria-live", politeness);
  region.setAttribute("aria-atomic", "true");
  region.setAttribute("style", SR_ONLY_STYLE);
  document.body.appendChild(region);
  return region;
}

/**
 * Makes sure both regions are in the page. Returns true when one of them had
 * to be created now (the caller waits a little longer before speaking).
 */
export function ensureLiveRegions(): boolean {
  if (!hasDom()) return false;
  let created = false;
  for (const politeness of ["polite", "assertive"] as const) {
    if (!findRegion(politeness)) {
      createRegion(politeness);
      created = true;
    }
  }
  return created;
}

/**
 * Says `message` once. The region is cleared first and filled a tick later,
 * so the same words twice in a row are announced twice. `assertive`
 * interrupts: keep it for errors that block what the person was doing.
 */
export function announce(message: string, politeness: AnnouncePoliteness = "polite"): void {
  const text = message.trim();
  if (!text || !hasDom()) return;
  const fresh = ensureLiveRegions();
  const region = findRegion(politeness);
  if (!region) return;
  region.textContent = "";
  if (timers[politeness]) clearTimeout(timers[politeness]);
  timers[politeness] = setTimeout(
    () => {
      const target = findRegion(politeness);
      if (target) target.textContent = text;
    },
    fresh ? FRESH_REGION_DELAY_MS : FILL_DELAY_MS,
  );
}

export interface UseAnnounceOptions {
  politeness?: AnnouncePoliteness;
  /**
   * `false`: what is there when the component mounts is part of the page and
   * is read in order, only later changes are said (a summary that updates
   * with each choice, «Guardado» after a save). Default `true`.
   */
  onMount?: boolean;
}

/**
 * Announces `message` when it appears or changes: the declarative twin of
 * `announce()` for UI that shows a status line («Guardado», «Te mandamos el
 * link»). Render the text visibly without `role="status"` and pass it here;
 * `null`, `false` or "" mean "nothing to say" (and re-arm the same text).
 */
export function useAnnounce(
  message: string | null | false | undefined,
  options: AnnouncePoliteness | UseAnnounceOptions = {},
): void {
  const { politeness = "polite", onMount = true } = typeof options === "string" ? { politeness: options } : options;
  const lastRef = useRef<string | null>(null);
  const mountedRef = useRef(false);
  const text = typeof message === "string" ? message.trim() : "";
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      if (!onMount) {
        lastRef.current = text || null;
        return;
      }
    }
    if (!text) {
      lastRef.current = null;
      return;
    }
    if (text === lastRef.current) return;
    lastRef.current = text;
    announce(text, politeness);
  }, [text, politeness, onMount]);
}

/**
 * `useAnnounce` as an element, for a branch of JSX that cannot call a hook
 * (after an early return, inside a map): renders nothing.
 */
export function Announce({
  message,
  politeness,
  onMount,
}: { message: string | null | false | undefined } & UseAnnounceOptions): null {
  useAnnounce(message, { politeness, onMount });
  return null;
}

/**
 * What a screen reader would say for `node`: its text without the parts
 * hidden from it (`aria-hidden`, `hidden`) or the controls inside it (a
 * «Reintentar» button is an action, not part of the message).
 */
export function announceableText(node: Node): string {
  const parts: string[] = [];
  const walk = (current: Node) => {
    if (current.nodeType === 3) {
      parts.push(current.textContent ?? "");
      return;
    }
    if (current.nodeType !== 1) return;
    const element = current as Element;
    if (
      element.getAttribute("aria-hidden") === "true" ||
      element.hasAttribute("hidden") ||
      element.tagName === "BUTTON" ||
      element.getAttribute("role") === "button"
    ) {
      return;
    }
    for (const child of Array.from(element.childNodes)) walk(child);
    parts.push(" ");
  };
  walk(node);
  return parts.join("").replace(/\s+/g, " ").trim();
}

/**
 * Ref for a status block whose content is markup rather than a string (a
 * Callout with a title and a body, a confirmation with a link): its text is
 * announced when the element mounts and again whenever it changes.
 * `enabled: false` keeps it quiet (e.g. a callout that is there from the
 * start of the page).
 */
export function useAnnounceRef<T extends Element>(
  politeness: AnnouncePoliteness = "polite",
  enabled = true,
): (node: T | null) => void {
  const observerRef = useRef<MutationObserver | null>(null);
  const lastRef = useRef("");
  return useCallback(
    (node: T | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      if (!node || !enabled) {
        lastRef.current = "";
        return;
      }
      const speak = () => {
        const text = announceableText(node);
        if (!text || text === lastRef.current) return;
        lastRef.current = text;
        announce(text, politeness);
      };
      speak();
      if (typeof MutationObserver === "undefined") return;
      observerRef.current = new MutationObserver(speak);
      observerRef.current.observe(node, { childList: true, subtree: true, characterData: true });
    },
    [politeness, enabled],
  );
}

export interface AnnouncedProps extends HTMLAttributes<HTMLElement> {
  /** The element it renders. Default `div`. */
  as?: "div" | "p" | "span" | "section";
  politeness?: AnnouncePoliteness;
}

/**
 * A status block whose text (markup, a `<Trans>`) is said when it mounts and
 * when it changes, through the app's live region: the element itself is not
 * a live region. For conditional JSX that cannot call `useAnnounceRef`.
 */
export function Announced({ as = "div", politeness = "polite", ...props }: AnnouncedProps) {
  const ref = useAnnounceRef<HTMLElement>(politeness);
  return createElement(as, { ...props, ref });
}

/**
 * Mount once at the root of every entry (app router, public pages): creates
 * the two regions on the first render, before anything has to be said.
 * Renders nothing; the regions live on <body>, outside the React tree.
 */
export function LiveAnnouncer(): null {
  useEffect(() => {
    ensureLiveRegions();
  }, []);
  return null;
}

/** Test helper: removes the regions and pending fills. */
export function resetLiveRegions(): void {
  for (const politeness of ["polite", "assertive"] as const) {
    if (timers[politeness]) clearTimeout(timers[politeness]);
    delete timers[politeness];
    if (hasDom()) findRegion(politeness)?.remove();
  }
}
